import { Directive, ElementRef, Input, Renderer2, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

export interface AccessibilityRule {
	selector?: string;

	context?: 'host' | 'shadow' | 'light';

	attributes: {
		[attr: string]: string | boolean | number;
	};
}

@Directive({
	selector: '[imvSetAccessibility]',
})
export class ImvSetAccessibilityDirective implements AfterViewInit, OnDestroy, OnChanges {
	@Input('imvSetAccessibility') rules: AccessibilityRule[] = [];

	private observer: MutationObserver | null = null;

	private elementObservers: MutationObserver[] = [];

	constructor(
		private el: ElementRef,

		private renderer: Renderer2,
	) {}

	ngAfterViewInit(): void {
		const host = this.el.nativeElement as Element;

		const shadowRoot = (host as any).shadowRoot as ShadowRoot | null;

		this.initObserver(host, shadowRoot);

		setTimeout(() => this.applyAttributes(), 0);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['rules']) {
			setTimeout(() => this.applyAttributes(), 0);
		}
	}

	ngOnDestroy(): void {
		this.elementObservers.forEach(obs => obs.disconnect());

		this.elementObservers = [];

		this.observer?.disconnect();

		this.observer = null;
	}

	private initObserver(host: Element, shadowRoot: ShadowRoot | null): void {
		this.observer = new MutationObserver(mutations => {
			this.handleMutations(host, shadowRoot, mutations);

			setTimeout(() => this.applyAttributes(), 1500);
		});

		this.observer.observe(host, { childList: true, subtree: true });

		if (shadowRoot) {
			this.observer.observe(shadowRoot, { childList: true, subtree: true });
		}
	}

	private handleMutations(host: Element, shadowRoot: ShadowRoot | null, mutations: MutationRecord[]): void {
		mutations.forEach(mutation => {
			if (mutation.type !== 'childList') {
				return;
			}

			this.processAddedNodes(host, shadowRoot, mutation.addedNodes);
		});
	}

	private processAddedNodes(host: Element, shadowRoot: ShadowRoot | null, nodes: NodeList): void {
		Array.from(nodes).forEach(node => {
			if (!(node instanceof Element)) {
				return;
			}

			const element = node as Element;

			this.rules.forEach(rule => {
				this.applyRuleForContext(rule, host, shadowRoot, element);
			});
		});
	}

	private applyRuleForContext(rule: AccessibilityRule, host: Element, shadowRoot: ShadowRoot | null, addedElement: Element): void {
		const ctx = rule.context ?? 'shadow';

		let targets: Element[] = [];

		if (ctx === 'host') {
			// ignore selector, apply straight to host

			targets = [host];
		} else if (ctx === 'light') {
			if (rule.selector) {
				targets = Array.from(host.querySelectorAll(rule.selector));
			}
		} else {
			// 'shadow'

			if (shadowRoot && rule.selector) {
				if (addedElement.matches(rule.selector)) {
					targets = [addedElement];
				} else {
					targets = Array.from(addedElement.querySelectorAll(rule.selector));
				}
			}
		}

		targets.forEach(el => this.applyAttributesToElement(el, rule.attributes));
	}

	private applyAttributes = (): void => {
		const host = this.el.nativeElement as Element;

		const shadowRoot = (host as any).shadowRoot as ShadowRoot | null;

		this.rules.forEach(rule => {
			const ctx = rule.context ?? 'shadow';

			if (ctx === 'host') {
				this.applyAttributesToElement(host, rule.attributes);
			} else if (ctx === 'light') {
				if (rule.selector) {
					const lightEls = Array.from(host.querySelectorAll(rule.selector));

					lightEls.forEach(el => this.applyAttributesToElement(el, rule.attributes));
				}
			} else {
				// shadow

				if (shadowRoot && rule.selector) {
					const shadowEls = Array.from(shadowRoot.querySelectorAll(rule.selector));

					shadowEls.forEach(el => {this.applyAttributesToElement(el, rule.attributes); if ('tabindex' in rule.attributes){this.observeTabIndex(el);}});
				}
			}
		});

		this.attemptDisconnectObserver(shadowRoot);
	};

	private applyAttributesToElement(el: Element, attrs: { [key: string]: string | boolean | number }): void {
		Object.entries(attrs).forEach(([key, value]) => {
			this.renderer.setAttribute(el, key, String(value));
		});
	}

	private attemptDisconnectObserver(shadowRoot: ShadowRoot | null): void {
		if (!this.observer) {
			return;
		}

		const allSatisfied = this.rules.every(rule => {
			const ctx = rule.context ?? 'shadow';

			if (ctx === 'host') {
				return true;
			}

			if (ctx === 'light') {
				if (rule.selector) {
					const host = this.el.nativeElement as Element;

					return !!host.querySelector(rule.selector);
				}

				return false;
			}

			// 'shadow'

			if (shadowRoot && rule.selector) {
				return !!shadowRoot.querySelector(rule.selector);
			}

			return false;
		});

		if (allSatisfied) {
			this.observer.disconnect();

			this.observer = null;
		}
	}
	private observeTabIndex(el: Element): void {
		const observer = new MutationObserver(mutations => {
			mutations.forEach(mutation => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'tabindex') {
					const currentValue = el.getAttribute('tabindex');
					if (currentValue && parseInt(currentValue, 10) > 0) {
						this.renderer.setAttribute(el, 'tabindex', '0');
					}
				}
			});
		});
		observer.observe(el, {
			attributes: true,
			attributeFilter: ['tabindex'],
		});

		// Opcional: Guarda en un array si quieres limpiar luego en ngOnDestroy
		this.elementObservers.push(observer);
	}
}
